import { Box } from "@mui/material";
import { Control } from "react-hook-form";

import CarriersSection from "./CarriersSection";
import CoveragesSection from "./CoveragesSection";
import { LoadingState, ErrorState } from "./LoadingErrorStates";
import OfflineSubmissionForm, {
  type OfflinePlansQuotedCoverage,
  type OfflineSubmissionFormData,
} from "./OfflineSubmissionForm";
import ReviewSection, { type EditedReviewFields } from "./ReviewSection";
import { STEP_CARRIERS, STEP_COVERAGES, STEP_REVIEW } from "./stepMachine";

import type { StepState } from "./stepMachine";
import type { SendRfpCarrier, SendRfpCoverage } from "./api";
import type {
  SendRfpClientDetails,
  SendRfpBrokerageDetails,
  SendRfpSubmissionData,
  CoverageNoteSection,
} from "./api/types";
import type { NotesTab } from "./constants";

interface StepContentProps {
  isLoading: boolean;
  isError: boolean;
  stepState: StepState;
  carriers: SendRfpCarrier[];
  coveragesWithCompletion: SendRfpCoverage[];
  clientDetails: SendRfpClientDetails | undefined;
  brokerageDetails: SendRfpBrokerageDetails | undefined;
  submissionData: SendRfpSubmissionData | undefined;
  eligibleEmployees: number | null;
  brokerPortalRfpId: string;
  rfpNotes: string;
  coverageNotes: Record<string, string>;
  coverageNoteSections: CoverageNoteSection[];
  analystDocsFolderUrl: string | undefined;
  offlineFormControl: Control<OfflineSubmissionFormData>;
  offlinePlansQuotedCoverages: OfflinePlansQuotedCoverage[];
  onCarriersChange: (carriers: string[]) => void;
  onCoveragesChange: (coverages: string[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  onOpenNotesDrawer: (tab: NotesTab) => void;
  onFieldsChange: (fields: EditedReviewFields | null) => void;
  onEditingChange?: (isEditing: boolean) => void;
  isStepCompleted: (step: string) => boolean;
}

const StepContent = ({
  isLoading,
  isError,
  stepState,
  carriers,
  coveragesWithCompletion,
  clientDetails,
  brokerageDetails,
  submissionData,
  eligibleEmployees,
  brokerPortalRfpId,
  rfpNotes,
  coverageNotes,
  coverageNoteSections,
  analystDocsFolderUrl,
  offlineFormControl,
  offlinePlansQuotedCoverages,
  onCarriersChange,
  onCoveragesChange,
  onNext,
  onBack,
  onSubmit,
  onOpenNotesDrawer,
  onFieldsChange,
  onEditingChange,
  isStepCompleted,
}: StepContentProps): React.JSX.Element => {
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;

  if (stepState.isOfflineSubmission) {
    return (
      <>
        <Box sx={{ mb: 3 }}>
          <OfflineSubmissionForm
            control={offlineFormControl}
            coverages={offlinePlansQuotedCoverages}
          />
        </Box>
        <CarriersSection
          carriers={carriers}
          selectedCarriers={stepState.selectedCarriers}
          onCarriersChange={onCarriersChange}
          eligibleEmployees={eligibleEmployees}
          isOfflineSubmission={stepState.isOfflineSubmission}
          active
          completed={false}
        />
        <ReviewSection
          rfpId={brokerPortalRfpId}
          state={stepState}
          clientDetails={clientDetails}
          brokerageDetails={brokerageDetails}
          effectiveDate={submissionData?.effectiveDate}
          carrierDueDate={submissionData?.carrierDueDate ?? undefined}
          onSubmit={onSubmit}
          onOpenNotesDrawer={onOpenNotesDrawer}
          onFieldsChange={onFieldsChange}
          onEditingChange={onEditingChange}
          rfpNotes={rfpNotes}
          coverageNotes={coverageNotes}
          coverageNoteSections={coverageNoteSections}
          analystDocsFolderUrl={analystDocsFolderUrl}
          active
          completed={false}
        />
      </>
    );
  }

  return (
    <>
      <CarriersSection
        carriers={carriers}
        selectedCarriers={stepState.selectedCarriers}
        onCarriersChange={onCarriersChange}
        eligibleEmployees={eligibleEmployees}
        isOfflineSubmission={stepState.isOfflineSubmission}
        onNext={onNext}
        active={stepState.currentStep === STEP_CARRIERS}
        completed={isStepCompleted(STEP_CARRIERS)}
      />
      <CoveragesSection
        coverages={coveragesWithCompletion}
        selectedCoverages={stepState.selectedCoverages}
        onCoveragesChange={onCoveragesChange}
        onNext={onNext}
        onBack={onBack}
        active={stepState.currentStep === STEP_COVERAGES}
        completed={isStepCompleted(STEP_COVERAGES)}
      />
      <ReviewSection
        rfpId={brokerPortalRfpId}
        state={stepState}
        clientDetails={clientDetails}
        brokerageDetails={brokerageDetails}
        effectiveDate={submissionData?.effectiveDate}
        carrierDueDate={submissionData?.carrierDueDate ?? undefined}
        onBack={onBack}
        onSubmit={onSubmit}
        onOpenNotesDrawer={onOpenNotesDrawer}
        onFieldsChange={onFieldsChange}
        onEditingChange={onEditingChange}
        rfpNotes={rfpNotes}
        coverageNotes={coverageNotes}
        coverageNoteSections={coverageNoteSections}
        analystDocsFolderUrl={analystDocsFolderUrl}
        active={stepState.currentStep === STEP_REVIEW}
        completed={isStepCompleted(STEP_REVIEW)}
      />
    </>
  );
};

export default StepContent;
